import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Kicker, SmchsLayout } from '@/components/smchs/Shell';
import { authApi } from '@/services/user-api';
import { getFieldErrors } from '@/services/api-base';

/**
 * Two states on one public route:
 *  - no `uid`/`token` in the URL: ask for the e-mail address (the backend answers the
 *    same way whether or not the address exists);
 *  - `?uid=&token=` from the e-mailed link: choose a new password.
 */
const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const uid = params.get('uid');
  const token = params.get('token');
  const hasLink = Boolean(uid && token);

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const inputClass =
    'w-full rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3.5 py-2.5 text-[14px] text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30';

  const requestLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return setErrors({ email: 'E-mail is required' });
    setIsLoading(true);
    setErrors({});
    try {
      await authApi.requestPasswordReset(email.trim());
      setSent(true);
    } catch (error) {
      setErrors(getFieldErrors(error));
    } finally {
      setIsLoading(false);
    }
  };

  const setPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return setErrors({ confirm_password: "Passwords don't match" });
    setIsLoading(true);
    setErrors({});
    try {
      await authApi.confirmPasswordReset({
        uid: uid as string,
        token: token as string,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success('Password updated. Sign in with your new password.');
      navigate('/signin');
    } catch (error) {
      setErrors(getFieldErrors(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SmchsLayout>
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="max-w-md">
          <Kicker label={hasLink ? 'Choose a new password' : 'Reset your password'} />
          <h1 className="mt-5 font-display font-[900] text-[2rem] md:text-[2.75rem] leading-[1] tracking-[-0.03em] text-[var(--ink)]">
            {hasLink ? 'Set a new password' : 'Forgotten your password?'}
          </h1>

          {!hasLink && sent && (
            <p className="mt-6 text-[15px] leading-relaxed text-[var(--ink-soft)]">
              If an account exists for that address, a reset link is on its way. The link is
              single-use and expires; request another if it stops working.
            </p>
          )}

          {!hasLink && !sent && (
            <form className="mt-7 space-y-5" onSubmit={requestLink}>
              <div>
                <label htmlFor="email" className="block text-[13px] font-medium text-[var(--ink)] mb-1.5">
                  School e-mail
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className={inputClass}
                />
                {errors.email && <p className="mt-1.5 text-[12px] text-red-500">{errors.email}</p>}
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--primary)] px-5 py-2.5 text-[14px] font-medium text-white hover:opacity-90 disabled:opacity-60"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                E-mail me a reset link
              </button>
            </form>
          )}

          {hasLink && (
            <form className="mt-7 space-y-5" onSubmit={setPassword}>
              <div>
                <label htmlFor="new_password" className="block text-[13px] font-medium text-[var(--ink)] mb-1.5">
                  New password
                </label>
                <input
                  id="new_password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                  className={inputClass}
                />
                {errors.new_password && <p className="mt-1.5 text-[12px] text-red-500">{errors.new_password}</p>}
              </div>
              <div>
                <label htmlFor="confirm_password" className="block text-[13px] font-medium text-[var(--ink)] mb-1.5">
                  Confirm password
                </label>
                <input
                  id="confirm_password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  className={inputClass}
                />
                {errors.confirm_password && <p className="mt-1.5 text-[12px] text-red-500">{errors.confirm_password}</p>}
                {errors.token && <p className="mt-1.5 text-[12px] text-red-500">{errors.token}</p>}
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--primary)] px-5 py-2.5 text-[14px] font-medium text-white hover:opacity-90 disabled:opacity-60"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Save new password
              </button>
            </form>
          )}

          <p className="mt-8 text-[13px] text-[var(--ink-soft)]">
            <Link to="/signin" className="font-medium text-[var(--primary)] hover:opacity-80">
              Back to sign in
            </Link>
          </p>
        </div>
      </section>
    </SmchsLayout>
  );
};

export default ResetPassword;
